'use es6';

import { createSelector } from 'reselect';
import Immutable from 'immutable';
import { ObjectCategory, RequestStatus } from 'FileManagerCore/Constants';
import { filterFolders, getParentFolder, getAncestors } from 'FileManagerCore/utils/FoldersAndFiles';
import { getFiles, getFilesTotal, getFetchFilesRequestStatus, getIsSearching, getIsFilteringArchived, getIsFilteringExtension, getIsFilteringType } from 'FileManagerCore/selectors/Files';
import { findFolderByPath, getFoldersById } from 'FileManagerCore/selectors/Folders';
import { getUploadingFiles } from 'FileManagerCore/selectors/UploadingFiles';
import { getSearchQueryFromUrl, getQueryWithDefaults, getQueryParams } from './Query';
import * as SortDirection from '../enums/SortDirection';
import { FOLDER_PATH_QUERY_PARAM_NAME, parseFileIdFromUrlParams } from '../utils/urls';
import { parseFolderIdFromLocation } from '../utils/location';
import { addLeadingSlashToPath } from 'FileManagerCore/utils/stringUtils';
import { getPageSize } from './pageSize';
import { getAssetHasTeam, getUserHasAccessToAsset } from 'FileManagerCore/utils/teams';
import { getIsUserSuperAdmin, getCurrentUserTeams, selectIsUngatedForBackendPoweredPagination } from 'FileManagerCore/selectors/Auth';
const NUMERIC_FIELDS = Immutable.Set(['size', 'updated', 'created']);
export const getFolderPathFromUrl = createSelector([getQueryParams], query => {
  const folderPath = query[FOLDER_PATH_QUERY_PARAM_NAME];
  if (folderPath && folderPath !== '/') {
    return addLeadingSlashToPath(folderPath);
  }
  return null;
});
export const getFolderIdFromUrl = (state, {
  location
} = {}) => parseFolderIdFromLocation(location);
export const getFileDetailsIdFromUrl = (state, {
  location
} = {}) => parseFileIdFromUrlParams(location);
export const getFolders = state => {
  return state.folders.get('objects').toList();
};
export const getIsFilteringFiles = createSelector([getIsFilteringArchived, getIsFilteringType], (filteringArchived, filteringType) => filteringArchived || filteringType);
export const getIsSearchingOrFiltering = createSelector([getIsSearching, getIsFilteringFiles, getIsFilteringExtension], (isSearching, isFiltering, isFilteringExtension) => isSearching || isFiltering || isFilteringExtension);
const getCombinedStatus = state => {
  const filesStatus = getFetchFilesRequestStatus(state);
  const foldersStatus = state.folders.get('status');
  if (filesStatus === RequestStatus.SUCCEEDED && foldersStatus === RequestStatus.SUCCEEDED) {
    return RequestStatus.SUCCEEDED;
  }
  if (filesStatus === RequestStatus.FAILED || foldersStatus === RequestStatus.FAILED) {
    return RequestStatus.FAILED;
  }
  if (filesStatus === RequestStatus.PENDING || foldersStatus === RequestStatus.PENDING) {
    return RequestStatus.PENDING;
  }
  if (filesStatus === RequestStatus.NOTFOUND || foldersStatus === RequestStatus.NOTFOUND) {
    return RequestStatus.NOTFOUND;
  }
  return RequestStatus.UNINITIALIZED;
};
const getCombinedFilesAndFolders = (query, folders, files, uploadingFiles) => {
  const {
    orderBy,
    sortDirection
  } = query;
  const comparator = (a, b) => {
    if (NUMERIC_FIELDS.includes(orderBy)) {
      const fieldA = a.get(orderBy) || 0;
      const fieldB = b.get(orderBy) || 0;
      if (sortDirection === SortDirection.DESC) {
        return fieldB - fieldA;
      }
      return fieldA - fieldB;
    }
    const fieldA = a.get(orderBy) || '';
    const fieldB = b.get(orderBy) || '';
    if (sortDirection === SortDirection.DESC) {
      return fieldB.localeCompare(fieldA);
    }
    return fieldA.localeCompare(fieldB);
  };
  if (orderBy === 'name') {
    return folders.sort(comparator).concat(uploadingFiles.toList().concat(files).sort(comparator));
  }
  return folders.concat(uploadingFiles.toList(), files).sort(comparator);
};
const filterFilesToCurrentFolder = (files, folders, currentFolderId, folderPath) => {
  if (currentFolderId) {
    return files.filter(f => f.get('folder_id') === currentFolderId);
  }
  if (folderPath) {
    const parentFolder = folders.find(folder => folder.get('full_path') === addLeadingSlashToPath(folderPath));
    if (parentFolder) {
      return files.filter(f => {
        return f.get('folder_id') === parentFolder.get('id');
      });
    }
  }
  return files;
};
export const getFilteredFolders = createSelector([getFoldersById, getFolderPathFromUrl, getSearchQueryFromUrl, getFolderIdFromUrl, getIsFilteringFiles, getCurrentUserTeams, getIsUserSuperAdmin, selectIsUngatedForBackendPoweredPagination], (folders, folderPath, searchQuery, folderId, isFilteringFiles, teams, isSuperAdmin, isUngatedForBackendPoweredPagination) => {
  if (isFilteringFiles) {
    return Immutable.List();
  }
  if (!isSuperAdmin && !isUngatedForBackendPoweredPagination) {
    folders = folders.filter(folder => getAssetHasTeam(folder.get('teams'), teams.map(t => t.id)));
  }
  return filterFolders(folders, folderPath, searchQuery, folderId).toList();
});
export const getFilteredFiles = createSelector([getFiles, getFolders, getFolderIdFromUrl, getFolderPathFromUrl, getIsSearchingOrFiltering], (files, folders, folderId, folderPath, isSearchingOrFiltering) => {
  if (isSearchingOrFiltering) {
    return files;
  }
  return filterFilesToCurrentFolder(files, folders, folderId, folderPath);
});
export const getFilteredUploadingFiles = createSelector([getUploadingFiles, getFolders, getFolderIdFromUrl, getFolderPathFromUrl, getSearchQueryFromUrl], (uploadingFiles, folders, folderId, folderPath, isSearchingOrFiltering) => {
  if (isSearchingOrFiltering) {
    return uploadingFiles;
  }
  return filterFilesToCurrentFolder(uploadingFiles, folders, folderId, folderPath);
});
export const getFoldersAndFiles = createSelector([getQueryWithDefaults, getFilteredFolders, getFilteredFiles, getFilesTotal, getFilteredUploadingFiles], (query, folders, files, filesTotal, uploadingFiles) => {
  const total = folders.size + filesTotal + uploadingFiles.size;
  const objects = getCombinedFilesAndFolders(query, folders, files, uploadingFiles);
  return {
    total,
    objects
  };
});
export const getCurrentPageOfFoldersAndFiles = createSelector([getFoldersAndFiles, getQueryWithDefaults, getPageSize], (allFoldersAndFiles, query, pageSize) => {
  const page = query.page || 1;
  return {
    total: allFoldersAndFiles.total,
    objects: allFoldersAndFiles.objects.slice((page - 1) * pageSize, page * pageSize)
  };
});
export const getStatus = createSelector([getCombinedStatus], status => status);
export const getIsFetchingMore = state => state.files.get('fetchingMore');
export const getParentFolderForObject = createSelector([(state, props) => props.object, getFolders], getParentFolder);
export const getCurrentFolder = createSelector([getFoldersById, getFolderPathFromUrl, getFolderIdFromUrl], (foldersById, folderPath, folderId) => {
  if (!(folderPath || folderId)) {
    return null;
  }
  if (folderId) {
    return foldersById.get(folderId);
  }
  return findFolderByPath(foldersById, addLeadingSlashToPath(folderPath));
});
export const getCurrentFolderAncestors = createSelector([getCurrentFolder, getFoldersById], getAncestors);
export const getHasAnyFolders = createSelector([getFoldersById], folders => !folders.isEmpty());
export const getHasAccessToFolder = (state, props) => {
  const currentFolder = getCurrentFolder(state, props);
  return getUserHasAccessToAsset(state, currentFolder && currentFolder.get('id'), ObjectCategory.FOLDER);
};